'use client'

import { useEffect } from 'react'
import { useCeleryWebSocketContext } from '@/contexts/celery-websocket-context'
import { useAuth } from '@/contexts/auth-context'
import { toast } from 'sonner'
import { setWithTTL } from '@/lib/utils/storage-with-ttl'
import { cache } from '@/lib/cache'
import {
  getExportedFileStorageKey,
  getExcelTaskLabel,
  parseExcelTaskType,
  type ExcelTaskType,
} from '@/lib/excel-task-types'

const EXPORTED_FILE_TTL_SECONDS = 15 * 60 // 15 минут

/**
 * Глобальный обработчик уведомлений.
 * Подписывается на все уведомления от WebSocket и показывает toast на любой странице.
 * Также сохраняет информацию о экспортированных файлах для восстановления кнопки скачивания.
 * Очищает кеш артикулов при завершении импорта.
 */
export function GlobalNotificationHandler() {
  const { user } = useAuth()
  const isGuestUser = user?.role === 'guest'
  const { onNotification } = useCeleryWebSocketContext()

  useEffect(() => {
    if (isGuestUser) {
      return
    }
    const unsubscribe = onNotification((notification) => {
      console.log('[GlobalNotificationHandler] Получено уведомление:', notification.event, notification.data)

      if (notification.event === 'export_completed' && notification.data.shop_id) {
        const excelTaskType = parseExcelTaskType(notification.data.task_type)
        if (!excelTaskType) {
          console.warn('[GlobalNotificationHandler] export_completed без известного task_type:', notification.data.task_type)
          return
        }

        const result = notification.data.result || {}
        const taskLabel = getExcelTaskLabel(excelTaskType)
        toast.success(`Экспорт ${taskLabel} завершен успешно`)

        const filename = result.filename || result.file_path?.split('/').pop() || `export_${Date.now()}.xlsx`
        const fileInfo = {
          taskId: notification.data.task_id,
          filename: filename,
        }

        setWithTTL(
          getExportedFileStorageKey(notification.data.shop_id, excelTaskType),
          fileInfo,
          EXPORTED_FILE_TTL_SECONDS,
        )

        window.dispatchEvent(new CustomEvent('export-completed', {
          detail: {
            shopId: notification.data.shop_id,
            fileInfo,
            taskType: excelTaskType,
          },
        }))
        return
      }

      if (notification.event === 'import_completed' && notification.data.shop_id) {
        const excelTaskType = parseExcelTaskType(notification.data.task_type)
        if (!excelTaskType) {
          console.warn('[GlobalNotificationHandler] import_completed без известного task_type:', notification.data.task_type)
          return
        }

        const result = notification.data.result || {}
        const taskLabel = getExcelTaskLabel(excelTaskType)
        const updatedCount = result.updated_count || 0
        const importedCount = result.imported_count || 0
        const createdCount = result.created_count || 0
        if (excelTaskType === 'change_log') {
          toast.success(
            `Импорт ${taskLabel} завершен: обработано ${importedCount} записей, создано ${createdCount}, обновлено ${updatedCount}`,
          )
        } else if (excelTaskType === 'plan_and_fact') {
          toast.success(
            `Импорт ${taskLabel} завершен: обработано ${importedCount} ячеек, обновлено ${updatedCount}`,
          )
        } else {
          toast.success(
            `Импорт ${taskLabel} завершен: обработано ${importedCount} артикулов, обновлено ${updatedCount}`,
          )
        }

        if (excelTaskType === 'articles') {
          cache.clear()
        }

        window.dispatchEvent(new CustomEvent('import-completed', {
          detail: {
            shopId: notification.data.shop_id,
            taskType: excelTaskType as ExcelTaskType,
          },
        }))
        return
      }

      if (notification.event === 'invoice_document_ready') {
        toast.success('Счёт готов к скачиванию')
        const paymentId = notification.data.result?.payment_id
        if (paymentId) {
          window.dispatchEvent(new CustomEvent('invoice-document-ready', {
            detail: { paymentId, taskId: notification.data.task_id },
          }))
        }
        return
      }

      if (notification.event === 'task_failed') {
        toast.error(`Ошибка: ${notification.data.error || 'Неизвестная ошибка'}`, {
          duration: Infinity,
        })

        if (notification.data.shop_id) {
          window.dispatchEvent(new CustomEvent('task-failed', {
            detail: { shopId: notification.data.shop_id },
          }))
        }
        return
      }

      if (notification.event === 'task_completed') {
        toast.success('Задача завершена успешно')
      }
    })

    return unsubscribe
  }, [onNotification, isGuestUser])

  return null
}
